<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-button @click.native="onShowModal(null)">Create custom sale</app-button>
      <app-cells><h2 class="title">Pending</h2></app-cells>
      <v-client-table
        :data="table_pending.items"
        :columns="table_pending.columns"
        :options="table_pending.options"
        class="table-default"
        :class="{ 'table-default--loading': table_pending_preloader }"
      >
        <template #legal_entity="{ row }">{{ row.legal_entity.short_name }}</template>
        <template #account="{ row }">{{ row.account.name }}</template>
        <template #date="{ row }">{{ dateFormat(row.date) }}</template>
        <template #supplier="{ row }">{{ row.supplier?.legal_name || '-' }}</template>
        <template #customer_name="{ row }">{{ row.customer_name || '-' }}</template>
        <template #notes="{ row }">{{ row.notes }}</template>
        <template #amount_vat="{ row }">{{ amountFormat(row.amount_vat) }} {{ row.account.currency.symbol }}</template>
        <template #amount_exempted="{ row }">{{ amountFormat(row.amount_exempted) }} {{ row.account.currency.symbol }}</template>
        <template #created_by="{ row }">
          <div class="table-default__controls">
            <div class="table-default__photo">
              <img
                :src="getUserAvatar(row.created_by?.crop?.crop_url)" alt="Avatar"
                v-tooltip="{ content: `${row.created_by?.full_name || ''}`, placement: 'top-start', offset: 10 }"
              >
            </div>
            {{ dateTimeFormat(row.created_at) }}
          </div>
        </template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onShowModal(row)" type="button">
              <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
            </button>
            <button @click="onShowDeleteModal(row.id)" type="button">
              <img src="@/assets/img/delete-table-icon.svg" alt="Delete">
            </button>
            <button @click="onShowModal(row, true)" v-tooltip="{ content: 'Create EFD', placement: 'top-start', offset: 10 }">
              <img src="@/assets/img/add-big-button-icon.svg" alt="Add">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPaginationPending"
            v-if="pagination_pending.pages > 1"
            v-model="pagination_pending.page"
            :page-count="pagination_pending.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination_pending.count" :count="table_pending.items.length" :page="pagination_pending.page" />
      </app-cells>
      <app-cells><h2 class="title">Issued</h2></app-cells>
      <v-client-table
        :data="table_issued.items"
        :columns="table_issued.columns"
        :options="table_issued.options"
        class="table-default"
        :class="{ 'table-default--loading': table_issued_preloader }"
      >
        <template #verification_code="{ row }">
          <button
            @click="onCopyToClipboard(row.efd_receipt.verification_code)"
            class="table-default__copy"
            :class="{'table-default__copy--disabled': !row.efd_receipt.verification_code}"
          >
            {{ row.efd_receipt.verification_code || '-' }}
          </button>
        </template>
        <template #legal_entity="{ row }">{{ row.legal_entity.short_name }}</template>
        <template #account="{ row }">{{ row.account.name }}</template>
        <template #date="{ row }">{{ dateFormat(row.date) }}</template>
        <template #supplier="{ row }">{{ row.supplier?.legal_name || '-' }}</template>
        <template #customer_name="{ row }">{{ row.customer_name || '-' }}</template>
        <template #notes="{ row }">{{ row.notes }}</template>
        <template #amount_vat="{ row }">{{ amountFormat(row.amount_vat) }} {{ row.account.currency.symbol }}</template>
        <template #amount_exempted="{ row }">{{ amountFormat(row.amount_exempted) }} {{ row.account.currency.symbol }}</template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPaginationIssued"
            v-if="pagination_issued.pages > 1"
            v-model="pagination_issued.page"
            :page-count="pagination_issued.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination_issued.count" :count="table_issued.items.length" :page="pagination_issued.page" />
      </app-cells>
    </template>
    <modal
      name="sale-modal"
      :width="700"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('sale-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">
          <template v-if="submit_receipt">Custom sale</template>
          <template v-else>{{ item_to_action ? 'Edit' : 'Create' }} custom sale</template>
        </div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm" class="arrange-popup">
            <app-grid>
              <template #item-1>
                <app-form-group label="Legal entity" required>
                  <app-select
                    v-model="form.legal_entity"
                    :options="legalEntities"
                    :reduce="item => item.code"
                    :clearable="false"
                    :readonly="submit_receipt"
                    label="short_name"
                    placeholder="Legal entity"
                    :error="$v.form.legal_entity.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.legal_entity.$dirty && !$v.form.legal_entity.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Account" required>
                  <app-select
                    v-model="form.account"
                    :reduce="item => item.id"
                    :options="finance_account_options"
                    :clearable="false"
                    :readonly="submit_receipt"
                    placeholder="Account"
                    :error="$v.form.account.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.account.$dirty && !$v.form.account.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
              <template #item-2>
                <app-form-group label="Date" required>
                  <app-date-picker
                    v-model="form.date"
                    :readonly="submit_receipt"
                    placeholder="Date"
                    :error="$v.form.date.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.date.$dirty && !$v.form.date.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group :label="is_supplier ? 'Supplier' : 'Customer'" label-for="customer_name" required>
                  <template #additional>
                    <app-switcher v-model="is_supplier" class="switcher--additional" :readonly="submit_receipt" />
                  </template>
                  <app-select
                    v-if="is_supplier"
                    @search="onSupplierSearch"
                    v-model="form.supplier"
                    :options="supplier_options"
                    :clearable="false"
                    :readonly="submit_receipt"
                    label="legal_name"
                    placeholder="Supplier"
                    :error="$v.form.supplier.$error"
                  />
                  <app-input
                    v-else
                    v-model="form.customer_name"
                    :error="$v.form.customer_name.$error"
                    id="customer_name"
                    placeholder="Customer"
                    :readonly="submit_receipt"
                  />
                  <template #error>
                    <div v-if="$v.form.supplier.$dirty && !$v.form.supplier.required">Required field</div>
                    <div v-if="$v.form.customer_name.$dirty && !$v.form.customer_name.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
            </app-grid>
            <div class="arrange-popup__part">
              <div class="arrange-popup__block-title arrange-popup__block-title--fz13"><span>Services</span></div>
              <div class="transfer-sortable transfer-sortable--full">
                <div v-for="(element, index) in form.services" :key="`own_item_${index}`" class="transfer-sortable__item transfer-sortable__item--mb-20">
                  <div class="transfer-sortable__row transfer-sortable__row--quart" :class="{ 'transfer-sortable__row--pr': !submit_receipt }">
                    <app-form-group label="Description" :label-for="`description-${index}`" required>
                      <app-input
                        v-model="element.description"
                        :error="$v.form.services.$each[index].description.$error"
                        placeholder="Write a description"
                        :id="`description-${index}`"
                        :readonly="submit_receipt"
                      />
                      <template #error>
                        <div v-if="$v.form.services.$each[index].description.$dirty && !$v.form.services.$each[index].description.required">Required field</div>
                      </template>
                    </app-form-group>
                    <app-form-group label="Tax code" required class="form-group--no-indent form-group--center">
                      <app-cells :indent="false">
                        <app-radio
                          v-for="radio of tax_code_options"
                          :key="radio.label"
                          v-model="element.tax_code"
                          :value="radio.value"
                          :name="`tax_code-${index}`"
                          :checked="element.tax_code === radio.value"
                          :readonly="submit_receipt"
                        >
                          <template #label>{{ radio.label }}</template>
                        </app-radio>
                      </app-cells>
                    </app-form-group>
                    <app-form-group label="Amount" :label-for="`amount-${index}`" required class="form-group--no-indent">
                      <app-fin-number
                        v-model="element.amount"
                        :error="$v.form.services.$each[index].amount.$error"
                        :id="`amount-${index}`"
                        placeholder="0"
                        :readonly="submit_receipt"
                      />
                      <template #error>
                        <div v-if="$v.form.services.$each[index].amount.$dirty && !$v.form.services.$each[index].amount.required">Required field</div>
                      </template>
                    </app-form-group>
                    <app-form-group label="Quantity" :label-for="`quantity-${index}`" required class="form-group--no-indent">
                      <app-input
                        v-model="element.quantity"
                        :error="$v.form.services.$each[index].quantity.$error"
                        :id="`quantity-${index}`"
                        placeholder="0"
                        type="number"
                        :readonly="submit_receipt"
                      />
                      <template #error>
                        <div v-if="$v.form.services.$each[index].quantity.$dirty && !$v.form.services.$each[index].quantity.required">Required field</div>
                      </template>
                    </app-form-group>
                    <template v-if="!submit_receipt">
                      <button
                        v-if="index"
                        @click="onRemoveItem(index)"
                        class="transfer-sortable__close"
                        type="button"
                      >
                        <img src="@/assets/img/delete-transfer.svg" alt="">
                      </button>
                      <button v-if="!index" @click="onAddItem" type="button" class="transfer-sortable__add"></button>
                    </template>
                  </div>
                </div>
              </div>
            </div>
            <app-form-group label="Additional notes" label-for="notes" required>
              <app-textarea
                v-model="form.notes"
                rows="5"
                :error="$v.form.notes.$error"
                placeholder="Write a note"
                id="notes"
                :readonly="submit_receipt"
              />
              <template #error>
                <div v-if="$v.form.notes.$dirty && !$v.form.notes.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submit">
                <template v-if="submit_receipt">Submit receipt</template>
                <template v-else>{{ item_to_action ? 'Save' : 'Create' }}</template>
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="delete"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete sale
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this sale?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteSale" ref="deleteSale">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deleteFinanceCustomSale,
  getAdministrativeSuppliers,
  getFinanceAccounts,
  getFinanceCustomSaleList,
  patchFinanceCustomSale,
  postFinanceCustomSale,
  postFinanceCustomSalesEFD,
} from '@/http'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { format, parseISO } from 'date-fns'
import debounce from 'lodash/debounce'

export default {
  name: 'FinanceEFDCustom',
  props: ['legalEntities'],
  data() {
    return {
      page_loaded: false,
      table_pending_preloader: false,
      table_issued_preloader: false,
      finance_account_options: [],
      supplier_options: [],
      is_supplier: true,
      tax_code_options: [
        { value: 'A', label: 'A' },
        { value: 'E', label: 'E' },
      ],
      form: {
        services: [{tax_code: 'A'}],
      },
      item_to_action: null,
      submit_receipt: false,
      table_pending: {
        items: [],
        columns: [
          'legal_entity',
          'account',
          'date',
          'supplier',
          'customer_name',
          'notes',
          'amount_vat',
          'amount_exempted',
          'created_by',
          'controls',
        ],
        options: {
          headings: {
            'legal_entity': 'Legal entity',
            'account': 'Account',
            'date': 'Date',
            'supplier': 'Supplier',
            'customer_name': 'Customer',
            'notes': 'Notes',
            'amount_vat': 'VAT',
            'amount_exempted': 'Exempted',
            'created_by': 'Created by',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_pending: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_issued: {
        items: [],
        columns: [
          'verification_code',
          'legal_entity',
          'account',
          'date',
          'supplier',
          'customer_name',
          'notes',
          'amount_vat',
          'amount_exempted',
        ],
        options: {
          headings: {
            'verification_code': 'Verification code',
            'legal_entity': 'Legal entity',
            'account': 'Account',
            'date': 'Date',
            'supplier': 'Supplier',
            'customer_name': 'Customer',
            'notes': 'Notes',
            'amount_vat': 'VAT',
            'amount_exempted': 'Exempted',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_issued: {
        pages: 1,
        page: 1,
        count: 1,
      },
    }
  },
  validations: {
    form: {
      legal_entity: { required },
      account: { required },
      date: { required },
      notes: { required },
      customer_name: {
        required: requiredIf(function() {
          return !this.is_supplier
        })
      },
      supplier: {
        required: requiredIf(function() {
          return this.is_supplier
        })
      },
      services: {
        $each: {
          description: { required },
          amount: { required },
          quantity: { required },
        }
      },
    }
  },
  created() {
    Promise.all([
      getFinanceAccounts(),
      this.fetchItemsPending(),
      this.fetchItemsIssued()
    ])
      .then(response => {
        this.finance_account_options = response[0].data
        this.page_loaded = true
      })
  },
  methods: {
    async fetchItemsPending() {
      this.table_pending_preloader = true
      const response = await getFinanceCustomSaleList({
        page: this.pagination_pending.page,
        page_size: 20,
        efd_receipt: false
      })
      this.table_pending.items = response.data.results
      this.pagination_pending.pages = response.data.pages
      this.pagination_pending.count = response.data.count
      this.table_pending_preloader = false
    },
    onClickPaginationPending(page) {
      this.pagination_pending.page = page
      this.fetchItemsPending()
    },
    async fetchItemsIssued() {
      this.table_issued_preloader = true
      const response = await getFinanceCustomSaleList({
        page: this.pagination_issued.page,
        page_size: 20,
        efd_receipt: true
      })
      this.table_issued.items = response.data.results
      this.pagination_issued.pages = response.data.pages
      this.pagination_issued.count = response.data.count
      this.table_issued_preloader = false
    },
    onClickPaginationIssued(page) {
      this.pagination_issued.page = page
      this.fetchItemsIssued()
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      if (this.item_to_action) {
        if (this.submit_receipt) {
          postFinanceCustomSalesEFD(this.item_to_action.id)
            .then(() => {
              this.fetchItemsPending()
              this.fetchItemsIssued()
              this.$notify({
                type: 'success',
                title: 'EFD created',
              })
              this.$modal.hide('sale-modal')
            })
            .catch(() => { this.$refs.submit.preload = false })
        } else {
          const data = { ...this.form }
          if (this.is_supplier) {
            data.customer_name = null
            data.supplier = data.supplier.id
          } else data.supplier = null
          patchFinanceCustomSale(this.item_to_action.id, data)
            .then(() => {
              this.fetchItemsPending()
              this.$notify({
                type: 'success',
                title: 'Changes saved',
              })
              this.$modal.hide('sale-modal')
            })
            .catch(() => { this.$refs.submit.preload = false })
        }
      } else {
        const data = { ...this.form }
        if (this.is_supplier) {
          delete data.customer_name
          data.supplier = data.supplier.id
        } else delete data.supplier
        postFinanceCustomSale(data)
          .then(() => {
            this.fetchItemsPending()
            this.$notify({
              type: 'success',
              title: 'Sale created',
            })
            this.$modal.hide('sale-modal')
          })
          .catch(() => { this.$refs.submit.preload = false })
      }
    },
    onDeleteSale() {
      this.$refs.deleteSale.preload = true
      deleteFinanceCustomSale(this.item_to_action)
        .then(() => {
          this.fetchItemsPending()
          this.$notify({
            type: 'success',
            title: 'Sale deleted',
          })
          this.$modal.hide('delete')
        })
        .catch(() => { this.$refs.deleteSale.preload = false })
    },
    onShowModal(item, submit_receipt = false) {
      this.item_to_action = item
      this.submit_receipt = submit_receipt
      if (item) {
        this.is_supplier = !!item.supplier
        this.form = {
          account: item.account.id,
          legal_entity: item.legal_entity.code,
          notes: item.notes,
          date: item.date,
          supplier: item.supplier,
          customer_name: item.customer_name,
          services: item.services.map(item => ({
            amount: item.amount,
            description: item.description,
            quantity: item.quantity,
            tax_code: item.tax_code,
          })),
        }
      } else {
        this.is_supplier = true
        this.form = {
          services: [{tax_code: 'A'}],
        }
      }
      this.$v.form.$reset()
      this.$modal.show('sale-modal')
    },
    onShowDeleteModal(item) {
      this.item_to_action = item
      this.$modal.show('delete')
    },
    onAddItem() {
      this.form.services.push({tax_code: 'A'})
    },
    onRemoveItem(index) {
      this.form.services.splice(index, 1)
    },
    amountFormat(amount) {
      return amount ? new Intl.NumberFormat('ru-RU').format(amount) : '-'
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    onCopyToClipboard(string) {
      navigator.clipboard.writeText(string)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => { console.error('Something went wrong') })
    },
    onSupplierSearch(search, loading) {
      loading(true)
      this.searchSupplier(loading, search, this)
    },
    searchSupplier: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.supplier_options = []
      } else {
        getAdministrativeSuppliers({search}).then(response => {
          vm.supplier_options = response.data
          loading(false)
        })
      }
    }, 350),
  }
}
</script>
